import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { setUserProperties, trackEvent } from '@/modules/analytics';
import { fetchStreamAuthToken, setPrimaryCoachForTeam } from '@/modules/api';
import type { MatchedCoaches } from '@/modules/api';
import { sendStreamMessage } from '@/modules/chat';
import type { User } from '@/types/user';

import type { ResourceState } from '../types/resolution';

type ScheduledConsult = { date: string; endDate?: string };

type UseCoachMatchingArgs = {
  /** The driver-fetched matched-coach catalog (raw resource state from useOnboardingResources). */
  matchedCoaches: ResourceState<MatchedCoaches>;
  /** Core-owned (read here in assign/intro); listed in the callbacks' deps. */
  userDataRef: RefObject<User | undefined>;
  /** Core-owned; captured by useOnboardingResources' ensureTeams, read by sendIntroMessage. */
  soloTeamUuidRef: RefObject<string | undefined>;
};

export type UseCoachMatching = {
  // Public view for the context value.
  matchedCoaches: MatchedCoaches | undefined;
  isLoadingCoaches: boolean;
  selectedCoachUuid: string | undefined;
  setSelectedCoach: (coachUuid: string | undefined) => void;
  scheduledConsult: ScheduledConsult | undefined;
  setScheduledConsult: (consult: ScheduledConsult | undefined) => void;
  assignPrimaryCoach: (coachUuid?: string) => Promise<void>;
  sendIntroMessage: (text: string) => Promise<boolean>;
  /** Clears the in-flow coach picks (per-user reset). */
  reset: () => void;
};

/**
 * Coach-matching sub-flow: the user's in-flow picks (selected coach + booked
 * consult) and the assign / intro-message actions. The matched-coach catalog
 * itself is a driver-fetched resource (useOnboardingResources) injected here as
 * a value; this hook only owns the transient picks. Extracted verbatim from
 * RulesEngineProvider.
 */
export function useCoachMatching({
  matchedCoaches,
  userDataRef,
  soloTeamUuidRef,
}: UseCoachMatchingArgs): UseCoachMatching {
  const [selectedCoachUuid, setSelectedCoachUuid] = useState<string | undefined>();
  const [scheduledConsult, setScheduledConsult] = useState<ScheduledConsult | undefined>();

  // Mirror the picks for the stable assign callback (its identity must not churn).
  const selectedCoachRef = useRef(selectedCoachUuid);
  const matchedCoachesRef = useRef<MatchedCoaches | undefined>(undefined);

  const matchedCoachesPublic = matchedCoaches.status === 'ready' ? matchedCoaches.value : undefined;
  const isLoadingCoaches = matchedCoaches.status === 'unset' || matchedCoaches.status === 'loading';

  // Picks a coach from the matched list (the card the user tapped). The coach
  // sub-flow's screen-to-screen advance is imperative (Continue handlers), so
  // this only records the selection.
  const setSelectedCoach = useCallback((coachUuid: string | undefined) => {
    setSelectedCoachUuid(coachUuid);
  }, []);

  // Assigns the selected coach as the solo team's primary coach (Sparta's
  // completeCoachMatching → setPrimaryCoachForTeam). Mirrors enrollInProgram but
  // deliberately does NOT flip the `teams` resource: the coach sub-flow then
  // advances imperatively (→ success → … → transition) and we don't want the
  // driver to yank to the terminal mid-flow. On refresh, the re-fetched `teams`
  // sees the primary coach and routes to the terminal. Throws on failure.
  const assignPrimaryCoach = useCallback(
    async (coachUuidArg?: string) => {
      const user = userDataRef.current;
      // Selection is keyed by the coach-profile `uuid` (the card identity); callers
      // that select + assign in one handler (CoachMatchingScreen's no-availability
      // skip) pass it explicitly since the ref isn't synced until the next commit.
      const selectedUuid = coachUuidArg ?? selectedCoachRef.current;
      const matched = matchedCoachesRef.current;
      const coach = matched?.coaches.find((c) => c.uuid === selectedUuid);
      const teamCode = user?.customerProfile?.soloTeamCode;
      // primary_coach expects the coach's USER uuid (Sparta's getSelectedCoachUuid
      // returns user.uuid), NOT the coach-profile uuid — send userUuid.
      if (!user || !coach?.userUuid || !teamCode) {
        throw new Error('Cannot assign coach: missing user, coach, or team code.');
      }
      await setPrimaryCoachForTeam({
        teamCode,
        coachUuid: coach.userUuid,
        batchUuid: matched?.batchUuid,
      });
      // Coach-assignment analytics (Sparta's completeCoachMatchingEpic success
      // block) — fired only after the assignment lands. `providerUuid` is the
      // coach's USER uuid (Sparta's getSelectedCoachUuid), matching the id sent to
      // the API. Pairs with the `hasAtLeastOnePrimaryCoach` person attribute Sparta
      // sets here; the group auto-assign path sets the same attribute in
      // enrollInProgram, so every coached user gets it regardless of path.
      trackEvent('provider-selected', {
        uuid: user.uuid,
        payingOrgUrn: user.payingOrganization?.urn,
        providerUuid: coach.userUuid,
        teamCode,
      });
      void setUserProperties({ hasAtLeastOnePrimaryCoach: true });
    },
    [userDataRef]
  );

  // Sends the chosen preset intro message into the new coach's care-team Stream
  // channel (Sparta's QuickIntroMessagesContainer send). Best-effort: any
  // failure — token, websocket, missing api key, channel not yet ready — is
  // swallowed so onboarding always advances (Sparta sends regardless of success
  // too). Reads userUuid + the captured solo-team uuid via refs.
  // Returns whether the message was actually sent (the intro-message analytics
  // distinguish send vs send-failed). Still best-effort — callers advance
  // regardless of the result.
  const sendIntroMessage = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        const userUuid = userDataRef.current?.uuid;
        const teamUuid = soloTeamUuidRef.current;
        if (!userUuid || !teamUuid || !text.trim()) {
          // Dev-only: surface WHICH precondition is missing. The most common
          // real-world cause is `teamUuid` being undefined — the solo team's
          // uuid is captured by useOnboardingResources' ensureTeams; if the v5
          // teams list never resolved (or carried no team matching the profile's
          // soloTeamCode) the ref stays undefined and the send is skipped.
          if (__DEV__) {
            console.warn('[sendIntroMessage] skipped — missing precondition', {
              userUuid,
              teamUuid,
              hasText: Boolean(text.trim()),
            });
          }
          return false;
        }
        const authToken = await fetchStreamAuthToken();
        if (!authToken) {
          if (__DEV__) console.warn('[sendIntroMessage] skipped — no Stream auth token');
          return false;
        }
        // sendStreamMessage returns false on a no-op (e.g. Stream API key unset),
        // so a no-send doesn't get mistaken for a success by the caller's analytics.
        return await sendStreamMessage({ userUuid, teamUuid, text, authToken });
      } catch (err) {
        // Best-effort — never block onboarding on a chat send failure. Log in
        // dev so the swallowed Stream error (connect / send / membership) is
        // visible while troubleshooting.
        if (__DEV__) console.warn('[sendIntroMessage] Stream send failed', err);
        return false;
      }
    },
    [userDataRef, soloTeamUuidRef]
  );

  const reset = useCallback(() => {
    setSelectedCoachUuid(undefined);
    setScheduledConsult(undefined);
  }, []);

  // Sync the picks into refs so the stable callbacks above read fresh values.
  useEffect(() => {
    selectedCoachRef.current = selectedCoachUuid;
    matchedCoachesRef.current =
      matchedCoaches.status === 'ready' ? matchedCoaches.value : undefined;
  });

  return {
    matchedCoaches: matchedCoachesPublic,
    isLoadingCoaches,
    selectedCoachUuid,
    setSelectedCoach,
    scheduledConsult,
    setScheduledConsult,
    assignPrimaryCoach,
    sendIntroMessage,
    reset,
  };
}
